import {
  EligibleGridColumnProps,
  EppsLoanInformation,
  UseEligibleGridColumnProps,
  useLoanDataFieldSelectorHook,
  useLookupOptions,
  useOriginalLoanInformationSelector,
} from '@frontend/pricing-search';
import { get } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getMIKey, useGetMI } from '../GetMIButton';
import { setSelectedMIAmount } from '../../../../data/lockSummary/actions';
import {
  useAdjustmentDetailsCache,
  useAttributionCategories,
  useLoanPropertiesSelector,
} from '../../../../data/useSelectors';
import { useIsHistoricalPricing } from '../../../../common/utils/customHooks/useIsHistoricalPricing';
import Session, {
  IS_BUYSIDE,
  IS_SELLSIDE,
} from '../../../../common/services/Session';
import { extractDescription } from './helpers';
import { useDisplayBuySideAdjustments } from '../../../../common/utils/customHooks/useDisplayBuySideAdjustments';
import { NON_CONVENTIONAL_PRODUCT_TYPES } from '../../helpers';

export interface UseEligibleProductsGridProps {
  disableLockLinks: boolean;
  isBuySideHistorical: boolean;
}

export const useEligibleProductsGrid = ({
  dispatch,
  loanId,
  relockRequest,
  lienPosition,
  productOptions,
  armIndexValue,
  mortgageType,
  transformRateSelectorDataToSlide,
  getAdjustmentDetails,
  filterPrograms,
  selectProduct,
  clientsettings,
  activeFilters,
  rows,
  arm,
  handleSelectProduct,
  handleSelectProductDetails,
  handleRefreshButton,
  updateAdjustmentDetailsCache,
  disableLockLinks,
  isBuySideHistorical,
  miCertification,
  proposedMIAmount,
}: UseEligibleGridColumnProps) => {
  const { store, handleGetMI } = useGetMI();
  const [selectedRow, setSelectedRow] = useState<any>(false);
  const clientSettings = useLookupOptions('clientSettings');
  const loanTermsLookup = (useLookupOptions('loanTerms') as any[]) || [];
  const originalLoanInformation = useOriginalLoanInformationSelector();
  const allCategories = useAttributionCategories();
  const loanAdjustments = useDisplayBuySideAdjustments();
  const changeAdditionalLoanProg =
    clientsettings[0]?.encompassSettings?.changeAdditionalLoanProg || false;
  const [slide, setSlide] = useState<any>({
    open: false,
    data: null,
    rowData: null,
    rateData: null,
  });
  const heloc = useMemo(() => mortgageType === 'HELOC', [mortgageType]);

  const handleSelectRow = (selection) => {
    setSelectedRow(selection);
    dispatch(selectProduct(selection));
    const { programID, rateDataID, productTypeID } = selection || {};
    const isMIEligible =
      !NON_CONVENTIONAL_PRODUCT_TYPES.includes(productTypeID);
    const miAmount =
      isMIEligible && miCertification
        ? proposedMIAmount
        : store.getRowState(getMIKey(rateDataID, programID)).miAmount ?? null;
    dispatch(setSelectedMIAmount(miAmount));
  };

  const adjustmentDetailsCache = useAdjustmentDetailsCache() || [];

  const loanData = useLoanDataFieldSelectorHook() as any;
  const buysideAdjustments = useSelector(
    ({ epps }) =>
      Object.values(epps.origin.buySideAdjustments ?? {}).flat(1) as any[],
  );
  const originalBuySideCustomAdjustments = useSelector(
    ({ EPPS }) =>
      EPPS?.originalLoanInformation?.rateLock?.buySideAdjustments
        ?.filter(
          (adj) =>
            adj?.adjustmentType === 'Adjustment' &&
            adj?.priceAdjustmentType === 'BaseRate',
        )
        .map(extractDescription) ?? [],
  );

  const { showMessage, isOpen } = useIsHistoricalPricing();
  const isHistoricalPricing = showMessage && isOpen;
  const isBuyside = Session.get(IS_BUYSIDE);
  const isSellside = Session.get(IS_SELLSIDE);

  const eppsUserName = loanData?.eppsUserName || null;
  useEffect(() => {
    if (slide.rowData && !slide.open) {
      const moveCustomAdjustmentToPrice = (adj) => {
        if (!adj.price) {
          adj.price = adj.rate;
          adj.rate = null;
        }
        return adj;
      };
      // These are modeled incorrectly
      // Fixing it here as we are end of release cycle
      // This should be fixed permanently when we fully move to EPC models in Partner UI
      const moveToPrice = (adj) => {
        if (adj.adjustmentType === 'CustomPriceAdjustment') return adj;
        adj.price = adj.rate ?? adj.price;
        delete adj.rate;
        return adj;
      };
      // we need to move custom adjustments to price if request action is 1:new lock
      if (loanData?.requestAction === 1) {
        buysideAdjustments
          .filter((adj) => adj.adjustmentType === 'CustomPriceAdjustment')
          .map((adj) => moveCustomAdjustmentToPrice(adj));
      }

      const filteredBuysideAdjustments = buysideAdjustments
        ?.filter((item1) => {
          return !originalBuySideCustomAdjustments?.some(
            (item2) =>
              item2?.description === item1?.description &&
              item1?.adjustmentType !== 'CustomPriceAdjustment',
          );
        })
        .map(extractDescription) as any[];

      originalBuySideCustomAdjustments?.forEach((item2) => {
        // Find item in original by description
        const foundIndex = filteredBuysideAdjustments.findIndex(
          (item1) => item1?.description === item2?.description,
        );

        if (foundIndex !== -1) {
          // Update existing item - update rate
          filteredBuysideAdjustments[foundIndex] = {
            ...filteredBuysideAdjustments[foundIndex],
            rate: item2?.rate,
          };
        } else {
          // Add new item with some default id and persist flags if missing
          filteredBuysideAdjustments.push({
            adjustmentType: 'CustomPriceAdjustment',
            description: item2?.description,
            id: item2?.id,
            persist: true,
            persistDisabled: false,
            rate: item2?.rate,
            price: null,
          });
        }
      });
      const loanCatcherAdjustmentTypes = [
        'LockExtensionAdjustment',
        'ReLockFeeAdjustment',
        'CustomPriceAdjustment',
        'CorporatePriceConcession',
        'BranchPriceConcession',
      ];
      const filteredLoanCatcherAdjustments = filteredBuysideAdjustments
        .filter((i) => loanCatcherAdjustmentTypes.includes(i.adjustmentType))
        .map((adj) => moveToPrice(adj));

      const { programID: programId, program } = slide.rowData;
      const rateDataId = slide.rateData
        ? slide.rateData
        : slide.rowData.rateDataID;
      const searchParams = {
        loanId,
        programId,
        rateDataId,
        rateEffectiveDate: slide.rowData.rateDate,
        lienPosition,
        eppsUserName,
        requestAction: loanData?.requestAction,
        loanChannel: loanData?.loanInformation?.loanChannel,
        wcpPolicyApplied: slide.rowData.wcpPolicyApplied,
      };
      const existingDetail = adjustmentDetailsCache.find(
        (detail) =>
          detail.programId === programId && detail.rateDataId === rateDataId,
      );
      if (existingDetail) {
        setSlide({
          open: true,
          data: existingDetail.dataSlide,
          rowData: null,
          rateData: null,
        });
      } else {
        dispatch(getAdjustmentDetails(searchParams)).then((response) => {
          if (response && response.adjustments) {
            const dataSlide = transformRateSelectorDataToSlide({
              armIndexValue,
              loanAdjustments,
              loanAdjustmentDetails: response.adjustments,
              attributionCategories: [
                ...allCategories.adjustmentReasonCategories,
                ...allCategories.paidByCategories,
              ],
            });
            dataSlide.showMarginTable = arm || heloc;
            dataSlide.bonaFideTestResults = response.bonaFideTestResults;
            dataSlide.programName = program;
            const updatedAdjustmentDetailsCache = [
              ...adjustmentDetailsCache,
              {
                programId,
                rateDataId,
                dataSlide,
              },
            ];
            dispatch(
              updateAdjustmentDetailsCache(updatedAdjustmentDetailsCache),
            );
            setSlide({
              open: true,
              data: dataSlide,
              rowData: null,
              rateData: null,
            });
          }
        });
      }
    }
  }, [slide]);

  const changeProgramOnReLock = get(
    clientsettings,
    '[0].encompassSettings.changeProgramOnReLock',
    true,
  );

  let filteredRows = filterPrograms(rows, activeFilters);

  const selectedProgram = filteredRows.find(
    (x) => x.programID === relockRequest?.specificProgramID,
  );

  useEffect(() => {
    // will reset store when rows change,
    // so that the MI amount is not persisted re-qualify
    store.reset();
  }, [rows]);

  useEffect(() => {
    if (selectedProgram && !selectedRow) {
      handleSelectRow(selectedProgram);
    }
  }, [selectedProgram?.programID]);

  if (
    relockRequest?.specificProgramID &&
    !changeProgramOnReLock &&
    !isBuyside &&
    !isSellside
  ) {
    filteredRows = filteredRows.filter(
      (x) => x.programID === relockRequest?.specificProgramID,
    );
  }

  if (
    relockRequest?.specificProgramID &&
    changeAdditionalLoanProg &&
    !selectedProgram
  ) {
    filteredRows = filterPrograms(rows, activeFilters);
  }

  return {
    filteredRows,
    isHistoricalPricing,
    selectedRow,
    handleSelectRow,
    slide,
    setSlide,
    handleSelectProduct,
    handleSelectProductDetails,
    handleRefreshButton,
    store,
    handleGetMI,
  };
};
